/**
 * Visualization-only statistics helpers.
 *
 * These mirror the backend's Wilson/Newcombe formulas (lambda/shared/statistics.js) so the daily
 * lift chart can shade a confidence band per day. They are NEVER the source of a headline number —
 * verdicts, CIs and precision labels shown as results always come from the payload (one engine).
 * By construction, the band's last point coincides with the backend CI over the same counts.
 */

const Z95 = 1.959963984540054

export interface ArmCounts {
  conversions: number
  sessions: number
}

export function wilsonCI(conversions: number, n: number, z = Z95): { lower: number; upper: number } {
  if (n <= 0) return { lower: 0, upper: 0 }
  const p = conversions / n
  const z2 = z * z
  const denom = 1 + z2 / n
  const center = (p + z2 / (2 * n)) / denom
  const half = (z * Math.sqrt((p * (1 - p)) / n + z2 / (4 * n * n))) / denom
  return { lower: Math.max(0, center - half), upper: Math.min(1, center + half) }
}

/** Newcombe (1998) hybrid-score CI on the ABSOLUTE difference variant − control. */
export function newcombeDiffCI(control: ArmCounts, variant: ArmCounts): { lower: number; upper: number } {
  const n1 = control.sessions
  const n2 = variant.sessions
  if (n1 <= 0 || n2 <= 0) return { lower: 0, upper: 0 }
  const p1 = control.conversions / n1
  const p2 = variant.conversions / n2
  const w1 = wilsonCI(control.conversions, n1)
  const w2 = wilsonCI(variant.conversions, n2)
  const diff = p2 - p1
  const lower = diff - Math.sqrt(Math.pow(p2 - w2.lower, 2) + Math.pow(w1.upper - p1, 2))
  const upper = diff + Math.sqrt(Math.pow(w2.upper - p2, 2) + Math.pow(p1 - w1.lower, 2))
  return { lower: Math.max(-1, lower), upper: Math.min(1, upper) }
}

export interface LiftSeriesPoint {
  date: string
  /** Cumulative hypothesis − baseline difference to this day, in FRACTION (not %). */
  lift: number
  lower: number
  upper: number
  /** Cumulative BASELINE rate at this point — denominator for the relative (%) display. */
  base_rate: number
  sessions: number
}

/**
 * Cumulative-to-date lift series in the HYPOTHESIS frame with a per-day Newcombe band.
 * As data accumulates the band narrows around the lift — the visual counterpart of
 * "direction settles before magnitude".
 *
 * `metric: 'auto'` prefers the goal series (`<arm>_goal_conversions`) when present, matching the
 * existing rate chart; 'purchase' forces the purchase series.
 */
export function buildCumulativeLiftSeries(
  timeSeries: any[] | undefined | null,
  isNegative: boolean,
  metric: 'auto' | 'goal' | 'purchase' = 'auto'
): LiftSeriesPoint[] {
  if (!timeSeries || timeSeries.length < 2) return []
  const hasGoal = timeSeries.some((d) => d.control_goal_conversions != null || d.treatment_goal_conversions != null)
  const useGoal = metric === 'goal' || (metric === 'auto' && hasGoal)
  if (useGoal && !hasGoal) return []

  const conv = (d: any, arm: 'control' | 'treatment') =>
    useGoal ? d[`${arm}_goal_conversions`] ?? 0 : d[`${arm}_conversions`] ?? 0

  const hypArm: 'control' | 'treatment' = isNegative ? 'control' : 'treatment'
  const baseArm: 'control' | 'treatment' = isNegative ? 'treatment' : 'control'

  const acc = {
    control: { conversions: 0, sessions: 0 },
    treatment: { conversions: 0, sessions: 0 },
  }
  const points: LiftSeriesPoint[] = []
  for (const d of timeSeries) {
    acc.control.sessions += d.control_sessions ?? 0
    acc.treatment.sessions += d.treatment_sessions ?? 0
    acc.control.conversions += conv(d, 'control')
    acc.treatment.conversions += conv(d, 'treatment')
    if (acc.control.sessions === 0 || acc.treatment.sessions === 0) continue
    const hyp = acc[hypArm]
    const base = acc[baseArm]
    const ci = newcombeDiffCI(base, hyp) // variant = hypothesis ⇒ diff already hyp − base
    points.push({
      date: d.date,
      lift: hyp.conversions / hyp.sessions - base.conversions / base.sessions,
      lower: ci.lower,
      upper: ci.upper,
      base_rate: base.conversions / base.sessions,
      sessions: acc.control.sessions + acc.treatment.sessions,
    })
  }
  return points
}
